import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography
} from "@mui/material";

import { Canvas } from "./components/Canvas/Canvas";
import { LeftSidebar } from "./components/LeftSidebar";
import { PreviewMode } from "./components/PreviewMode";
import { SettingsPanel } from "./components/SettingsPanel/SettingsPanel";
import { TopBar } from "./components/TopBar";
import { ReportGeneratorProvider, useReportGeneratorContext } from "./context/ReportGenerator-context";

const ReportGeneratorContent: React.FC = () => {
  const ctx = useReportGeneratorContext();

  if (ctx.previewMode) {
    return (
      <PreviewMode
        ref={ctx.previewRef}
        cards={ctx.cards}
        pages={ctx.pages}
        reportMeta={ctx.reportMeta}
        period={ctx.period}
        currency={ctx.currency}
        orientation={ctx.orientation}
        filters={ctx.filters}
        expandedRows={ctx.expandedRows}
        onToggleExpand={ctx.handleToggleExpand}
        autoExport={ctx.autoExport}
        onBack={() => {
          ctx.setPreviewMode(false);
          ctx.setAutoExport(false);
        }}
        onExportPDF={ctx.exportPreviewPdf}
      />
    );
  }

  const isBatchExporting = Boolean(ctx.batchExportProgress);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: "#cfd8e8",
        fontFamily: "'Roboto', 'Segoe UI', sans-serif"
      }}
    >
      <TopBar
        reportTitle={ctx.reportMeta.title}
        onTitleChange={(value) => ctx.setReportMeta((cur) => ({ ...cur, title: value }))}
        dateRange={ctx.dateRange}
        onDateRangeChange={ctx.onDateRangeChange}
        orientation={ctx.orientation}
        onOrientationChange={ctx.setOrientation}
        filters={ctx.filters}
        onFiltersChange={ctx.setFilters}
        onPreview={() => ctx.setPreviewMode(true)}
        onExportPDF={() => {
          ctx.setAutoExport(false);
          ctx.setPreviewMode(true);
        }}
        onBatchExport={ctx.handleStartBatchExport}
        canUndo={ctx.canUndo}
        canRedo={ctx.canRedo}
        onUndo={ctx.undo}
        onRedo={ctx.redo}
      />

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <LeftSidebar
          views={ctx.views}
          activeViewId={ctx.activeViewId}
          activeTab={ctx.sidebarTab}
          hasCards={ctx.cards.length > 0}
          onTabChange={ctx.setSidebarTab}
          onLoadView={ctx.loadView}
          onDeleteView={ctx.deleteView}
          onSaveView={ctx.saveView}
          onUpdateView={ctx.updateView}
          onAutoClean={ctx.autoCleanLayout}
          onLoadLayout={ctx.onLoadLayout}
          onClearCanvas={ctx.onClearCanvas}
          setDroppingItem={ctx.setDroppingItem}
        />

        <Canvas
          cards={ctx.cards}
          pages={ctx.pages}
          selectedCardId={ctx.selectedCardId}
          reportMeta={ctx.reportMeta}
          orientation={ctx.orientation}
          isResizing={ctx.isResizing}
          droppingItem={ctx.droppingItem}
          onResizingChange={ctx.setIsResizing}
          onUpdateMeta={(updates) => ctx.setReportMeta((cur) => ({ ...cur, ...updates }))}
          onLogoFileChange={ctx.setLogoFile}
          onSelectCard={ctx.setSelectedCardId}
          onRemoveCard={ctx.removeCard}
          onDuplicateCard={ctx.duplicateCard}
          onUpdateCard={ctx.updateCard}
          onPresetChange={ctx.onPresetChange}
          expandedRows={ctx.expandedRows}
          onToggleExpand={ctx.handleToggleExpand}
          onAddPage={ctx.addPage}
          onRemovePage={ctx.removePage}
          onUpdatePage={ctx.updatePage}
          onLayoutChange={ctx.onLayoutChange}
          onDropCard={ctx.onDropCard}
          onOpenSettings={ctx.openSettings}
        />

        {ctx.selectedCard && ctx.settingsOpen && (
          <SettingsPanel
            card={ctx.selectedCard}
            onUpdateConfig={(config) => ctx.updateCard(ctx.selectedCard!.id, { config })}
            onUpdateCardFilters={(cardFilters) => {
              const currentConfig = ctx.selectedCard!.config;
              ctx.updateCard(ctx.selectedCard!.id, {
                config: { ...currentConfig, cardFilters }
              });
            }}
            onClose={() => ctx.setSettingsOpen(false)}
          />
        )}
      </Box>

      {ctx.batchExportInvestors && (
        <BatchConfirmDialog
          investors={ctx.batchExportInvestors}
          reportTitle={ctx.reportMeta.title}
          onConfirm={ctx.handleConfirmBatchExport}
          onCancel={() => ctx.setBatchExportInvestors(null)}
        />
      )}

      {isBatchExporting && (
        <>
          <Box
            sx={{
              position: "fixed",
              left: -9999,
              top: 0,
              width: "auto",
              height: "auto",
              overflow: "hidden",
              pointerEvents: "none"
            }}
          >
            <PreviewMode
              ref={ctx.batchPreviewRef}
              cards={ctx.batchCards}
              pages={ctx.pages}
              reportMeta={ctx.reportMeta}
              period={ctx.period}
              currency={ctx.currency}
              orientation={ctx.orientation}
              filters={ctx.filters}
              expandedRows={ctx.expandedRows}
              onToggleExpand={ctx.handleToggleExpand}
              autoExport
              onBack={() => {}}
              onExportPDF={async () => {}}
            />
          </Box>
          <BatchProgressOverlay
            current={ctx.batchExportProgress!.current}
            total={ctx.batchExportProgress!.total}
            investor={ctx.batchExportProgress!.investor}
          />
        </>
      )}
    </Box>
  );
};

export const ReportGenerator: React.FC = () => {
  return (
    <ReportGeneratorProvider>
      <ReportGeneratorContent />
    </ReportGeneratorProvider>
  );
};

// ─── Batch export dialogs ─────────────────────────────────────────────────────

interface BatchConfirmDialogProps {
  investors: string[];
  reportTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const BatchConfirmDialog: React.FC<BatchConfirmDialogProps> = ({ investors, reportTitle, onConfirm, onCancel }) => {
  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: "#071f35", pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#071f35" }}>Batch Export</Typography>
          <IconButton onClick={onCancel}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#496682", mb: 2 }}>
          {investors.length} PDF{investors.length !== 1 ? "s" : ""} will be generated, one per investor:
        </Typography>
        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {investors.map((investor, i) => (
            <Box
              key={investor}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                p: "10px 14px",
                bgcolor: i % 2 === 0 ? "#f6f8fc" : "#FFFFFF",
                borderRadius: "8px",
                mb: "4px"
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "6px",
                  bgcolor: "#E8F5F0",
                  color: "#071f35",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                {i + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#071f35" }}>{investor}</Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#92a7bc",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {reportTitle} - {investor}.pdf
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: "16px 24px", borderTop: "1px solid #dbe8f5" }}>
        <Button onClick={onCancel} sx={{ borderRadius: "999px", color: "#496682", textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            borderRadius: "999px",
            bgcolor: "#071f35",
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": { bgcolor: "#0a2e4f" }
          }}
        >
          Export {investors.length} PDF{investors.length !== 1 ? "s" : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface BatchProgressOverlayProps {
  current: number;
  total: number;
  investor: string;
}

const BatchProgressOverlay: React.FC<BatchProgressOverlayProps> = ({ current, total, investor }) => {
  const pct = Math.round((current / total) * 100);
  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", p: "32px 36px", textAlign: "center" } }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#071f35", mb: "6px" }}>Exporting reports...</Typography>
      <Typography sx={{ fontSize: 13, color: "#496682", mb: "20px" }}>
        {current} of {total}:{" "}
        <Box component="span" sx={{ fontWeight: 600, color: "#071f35" }}>
          {investor}
        </Box>
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: "999px",
          bgcolor: "#D6F4EF",
          "& .MuiLinearProgress-bar": { bgcolor: "#071f35", borderRadius: "999px" }
        }}
      />
      <Typography sx={{ fontSize: 11, color: "#92a7bc", mt: 1 }}>{pct}% complete</Typography>
    </Dialog>
  );
};
